import Button from "./Button";
import { PopupModal } from "./Modal";
import { Tag } from "./Tag";
import type {
  CampaignRegenerationState,
  PublishedCampaign,
} from "../types/competitors";

function campaignTag(campaign: PublishedCampaign) {
  if (!campaign.createdByPipeline) {
    return <Tag tone="amber">not created by the pipeline</Tag>;
  }
  if (campaign.status === "ENABLED" || campaign.impressions > 0) {
    return <Tag tone="amber">live</Tag>;
  }
  return <Tag tone="neutral">paused · never served</Tag>;
}

function afterwards(state: CampaignRegenerationState): string {
  return state.publishesOnCompletion
    ? "The pipeline publishes the new set when generation finishes."
    : "The new set publishes when the customer finishes onboarding.";
}

/**
 * The warning before a regeneration that supersedes campaigns already in the
 * account: which ones, whether each is live, and what happens to them. The
 * backend writes the summary from the account itself; this lays it out and
 * holds the confirm.
 */
export function RegenerateCampaignsModal({
  state,
  saveFirst = false,
  pending,
  onCancel,
  onConfirm,
}: {
  state: CampaignRegenerationState;
  /** The editor's unsaved answers are saved before the regeneration starts. */
  saveFirst?: boolean;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const existing = state.campaigns.length > 0;
  return (
    <PopupModal
      onClose={pending ? undefined : onCancel}
      contentClassName="w-[560px] max-w-[92vw] mx-auto"
    >
      <div className="text-lg font-semibold text-content-primary">
        {existing
          ? "Regenerate and replace the campaigns?"
          : "Regenerate the campaigns?"}
      </div>

      <div className="text-sm text-content-secondary">
        {state.warning ??
          "Rebuilds the campaigns from the saved answers."}{" "}
        {afterwards(state)}
      </div>

      {existing && (
        <ul className="flex flex-col gap-1.5 text-sm">
          {state.campaigns.map((campaign) => (
            <li
              key={campaign.resourceName}
              className="flex items-center gap-2 flex-wrap"
            >
              <span className="font-medium text-content-primary">
                {campaign.name}
              </span>
              {campaignTag(campaign)}
              {campaign.impressions > 0 && (
                <span className="text-xs text-content-secondary">
                  {campaign.impressions.toLocaleString()} impressions
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="text-xs text-content-secondary">
        {saveFirst && "Your edits are saved first; generation reads the saved answers. "}
        Generation takes a few minutes. Nothing is deleted or paused by this
        action.
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button size="sm" loading={pending} onClick={onConfirm}>
          {saveFirst
            ? "Save & regenerate"
            : existing
              ? "Regenerate & republish"
              : "Regenerate"}
        </Button>
      </div>
    </PopupModal>
  );
}
